import { costCenterMasterService, productMasterService, wbsMasterService } from "@/apis/services"
import { ICostCenterMaster, IProductMaster, ISapReservationDetail, IWbsMaster } from "@/types"
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons"
import { AutoComplete, Button, Empty, Input, InputNumber, Space, Spin, Table } from "antd"
import { ColumnsType } from "antd/lib/table"
import { DefaultOptionType } from "antd/lib/select"
import { Dispatch, ReactNode, SetStateAction, useEffect, useRef, useState } from "react"

interface Props {
  items: ISapReservationDetail[]
  editable?: boolean
  onChange?: (items: ISapReservationDetail[]) => void
}

const createEmptyRow = (): ISapReservationDetail => ({
  product_id: "",
  qty: 1,
  uom: "",
  long_desc: "",
  wbs_code: "",
  wbs_description: "",
  cost_center_code: "",
  cost_center_name: "",
})

const normalizeEditableNumber = (value: ISapReservationDetail["qty"]): number | null => {
  if (value === null || value === undefined || value === "") {
    return null
  }

  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

interface IProductMasterOption extends DefaultOptionType {
  value: string
  label: ReactNode
  master: IProductMaster
}

interface IWbsMasterOption extends DefaultOptionType {
  value: string
  label: ReactNode
  master: IWbsMaster
}

interface ICostCenterMasterOption extends DefaultOptionType {
  value: string
  label: ReactNode
  master: ICostCenterMaster
}

const ReservationItemsTable = ({ items, editable = false, onChange }: Props) => {
  const [productOptionsMap, setProductOptionsMap] = useState<Record<number, IProductMasterOption[]>>({})
  const [wbsOptionsMap, setWbsOptionsMap] = useState<Record<number, IWbsMasterOption[]>>({})
  const [costCenterOptionsMap, setCostCenterOptionsMap] = useState<Record<number, ICostCenterMasterOption[]>>({})
  const [productLoadingMap, setProductLoadingMap] = useState<Record<number, boolean>>({})
  const [wbsLoadingMap, setWbsLoadingMap] = useState<Record<number, boolean>>({})
  const [costCenterLoadingMap, setCostCenterLoadingMap] = useState<Record<number, boolean>>({})
  const searchTimeoutRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({})
  const getRowKey = (row: ISapReservationDetail, index?: number) => row._id || `reservation-row-${index ?? 0}`

  useEffect(() => {
    const timeouts = searchTimeoutRef.current

    return () => {
      Object.values(timeouts).forEach(timeout => clearTimeout(timeout))
    }
  }, [])

  const updateRow = (index: number, key: keyof ISapReservationDetail, value: string | number | null) => {
    if (!onChange) return
    const nextItems = items.map((item, itemIndex) => (itemIndex === index ? { ...item, [key]: value ?? "" } : item))
    onChange(nextItems)
  }

  const updateRowBulk = (index: number, patch: Partial<ISapReservationDetail>) => {
    if (!onChange) return
    const nextItems = items.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item))
    onChange(nextItems)
  }

  const addRow = () => {
    if (!onChange) return
    onChange([...(items || []), createEmptyRow()])
  }

  const removeRow = (index: number) => {
    if (!onChange) return
    const nextItems = items.filter((_, itemIndex) => itemIndex !== index)
    onChange(nextItems.length ? nextItems : [createEmptyRow()])
  }

  const handleAutocompleteSearch = <TOption extends DefaultOptionType,>(
    key: string,
    index: number,
    setLoading: Dispatch<SetStateAction<Record<number, boolean>>>,
    setOptions: Dispatch<SetStateAction<Record<number, TOption[]>>>,
    request: () => Promise<TOption[]>
  ) => {
    if (searchTimeoutRef.current[key]) {
      clearTimeout(searchTimeoutRef.current[key])
    }

    setLoading(prev => ({ ...prev, [index]: true }))
    searchTimeoutRef.current[key] = setTimeout(() => {
      void (async () => {
        try {
          const nextOptions = await request()
          setOptions(prev => ({ ...prev, [index]: nextOptions }))
        } catch {
          setOptions(prev => ({ ...prev, [index]: [] }))
        } finally {
          setLoading(prev => ({ ...prev, [index]: false }))
        }
      })()
    }, 300)
  }

  const handleProductSearch = (index: number, keyword: string) => {
    updateRow(index, "product_id", keyword)
    const normalizedKeyword = String(keyword || "").trim()

    void handleAutocompleteSearch<IProductMasterOption>(
      `product-${index}`,
      index,
      setProductLoadingMap,
      setProductOptionsMap,
      async () => {
        const response = await productMasterService.getAutocompleteOptions(normalizedKeyword, 10)
        return (response.data.items || []).map(item => ({
          value: item.product_id || "",
          label: (
            <div style={{ display: "flex", flexDirection: "column" }}>
              <span>{item.product_id}</span>
              <span style={{ fontSize: 12, color: "#8c8c8c" }}>
                {item.uom || "-"} • {item.long_desc || "-"}
              </span>
            </div>
          ),
          master: item,
        }))
      }
    )
  }

  const handleProductSelect = (index: number, option?: DefaultOptionType) => {
    const pickedOption = option as IProductMasterOption | undefined
    if (!pickedOption?.master) return

    updateRowBulk(index, {
      product_id: pickedOption.master.product_id || "",
      uom: pickedOption.master.uom || "",
      long_desc: pickedOption.master.long_desc || "",
    })
  }

  const handleWbsSearch = (index: number, keyword: string) => {
    updateRowBulk(index, {
      wbs_code: keyword,
      wbs_description: "",
    })
    const normalizedKeyword = String(keyword || "").trim()

    void handleAutocompleteSearch<IWbsMasterOption>(
      `wbs-${index}`,
      index,
      setWbsLoadingMap,
      setWbsOptionsMap,
      async () => {
        const response = await wbsMasterService.getAutocompleteOptions(normalizedKeyword, 10)
        return (response.data.items || []).map(item => ({
          value: item.project_element_id || "",
          label: (
            <div style={{ display: "flex", flexDirection: "column" }}>
              <span>{item.project_element_id}</span>
              <span style={{ fontSize: 12, color: "#8c8c8c" }}>{item.description || "-"}</span>
            </div>
          ),
          master: item,
        }))
      }
    )
  }

  const handleWbsSelect = (index: number, option?: DefaultOptionType) => {
    const pickedOption = option as IWbsMasterOption | undefined
    if (!pickedOption?.master) return

    updateRowBulk(index, {
      wbs_code: pickedOption.master.project_element_id || "",
      wbs_description: pickedOption.master.description || "",
    })
  }

  const handleCostCenterSearch = (index: number, keyword: string) => {
    updateRowBulk(index, {
      cost_center_code: keyword,
      cost_center_name: "",
    })
    const normalizedKeyword = String(keyword || "").trim()

    void handleAutocompleteSearch<ICostCenterMasterOption>(
      `cost-center-${index}`,
      index,
      setCostCenterLoadingMap,
      setCostCenterOptionsMap,
      async () => {
        const response = await costCenterMasterService.getAutocompleteOptions(normalizedKeyword, 10)
        return (response.data.items || []).map(item => ({
          value: item.cost_center || "",
          label: (
            <div style={{ display: "flex", flexDirection: "column" }}>
              <span>{item.cost_center}</span>
              <span style={{ fontSize: 12, color: "#8c8c8c" }}>
                {item.name || "-"}{item.company_code ? ` • ${item.company_code}` : ""}
              </span>
            </div>
          ),
          master: item,
        }))
      }
    )
  }

  const handleCostCenterSelect = (index: number, option?: DefaultOptionType) => {
    const pickedOption = option as ICostCenterMasterOption | undefined
    if (!pickedOption?.master) return

    updateRowBulk(index, {
      cost_center_code: pickedOption.master.cost_center || "",
      cost_center_name: pickedOption.master.name || "",
    })
  }

  const columns: ColumnsType<ISapReservationDetail> = [
    {
      title: "No",
      key: "no",
      width: 72,
      render: (_value, _record, index) => index + 1,
    },
    {
      title: "Product ID *",
      dataIndex: "product_id",
      key: "product_id",
      width: 260,
      render: (value: ISapReservationDetail["product_id"], _record: ISapReservationDetail, index: number): ReactNode =>
        editable ? (
          <AutoComplete
            value={String(value || "")}
            options={productOptionsMap[index] || []}
            filterOption={false}
            onSearch={keyword => handleProductSearch(index, keyword)}
            onSelect={(_value, option) => handleProductSelect(index, option)}
            onFocus={() => handleProductSearch(index, String(value || ""))}
            notFoundContent={productLoadingMap[index] ? <div style={{ textAlign: "center", padding: "8px 0" }}><Spin size="small" /></div> : "No Product Master found"}
            style={{ width: "100%" }}
          >
            <Input placeholder="Item code or description" />
          </AutoComplete>
        ) : (
          value || "-"
        ),
    },
    {
      title: "Qty *",
      dataIndex: "qty",
      key: "qty",
      width: 140,
      render: (value: ISapReservationDetail["qty"], _record: ISapReservationDetail, index: number): ReactNode =>
        editable ? <InputNumber min={0.01} value={normalizeEditableNumber(value)} style={{ width: "100%" }} onChange={nextValue => updateRow(index, "qty", nextValue)} /> : value || "-",
    },
    {
      title: "UOM *",
      dataIndex: "uom",
      key: "uom",
      width: 160,
      render: (value: ISapReservationDetail["uom"], _record: ISapReservationDetail, index: number): ReactNode =>
        editable ? <Input value={value} placeholder="UOM" onChange={event => updateRow(index, "uom", event.target.value)} /> : value || "-",
    },
    {
      title: "Long Description *",
      dataIndex: "long_desc",
      key: "long_desc",
      width: 260,
      render: (value: ISapReservationDetail["long_desc"], _record: ISapReservationDetail, index: number): ReactNode =>
        editable ? <Input.TextArea value={value} placeholder="Enter long description" autoSize={{ minRows: 1, maxRows: 3 }} onChange={event => updateRow(index, "long_desc", event.target.value)} /> : value || "-",
    },
    {
      title: "WBS",
      dataIndex: "wbs_code",
      key: "wbs_code",
      width: 240,
      render: (value: ISapReservationDetail["wbs_code"], _record: ISapReservationDetail, index: number): ReactNode =>
        editable ? (
          <AutoComplete
            value={String(value || "")}
            options={wbsOptionsMap[index] || []}
            filterOption={false}
            onSearch={keyword => handleWbsSearch(index, keyword)}
            onSelect={(_value, option) => handleWbsSelect(index, option)}
            onFocus={() => handleWbsSearch(index, String(value || ""))}
            notFoundContent={wbsLoadingMap[index] ? <div style={{ textAlign: "center", padding: "8px 0" }}><Spin size="small" /></div> : "No WBS Master found"}
            style={{ width: "100%" }}
          >
            <Input placeholder="Search WBS code or description" />
          </AutoComplete>
        ) : (
          value || "-"
        ),
    },
    {
      title: "WBS Description",
      dataIndex: "wbs_description",
      key: "wbs_description",
      width: 240,
      render: (value: ISapReservationDetail["wbs_description"]): ReactNode => value || "-",
    },
    {
      title: "Cost Center",
      dataIndex: "cost_center_code",
      key: "cost_center_code",
      width: 240,
      render: (value: ISapReservationDetail["cost_center_code"], _record: ISapReservationDetail, index: number): ReactNode =>
        editable ? (
          <AutoComplete
            value={String(value || "")}
            options={costCenterOptionsMap[index] || []}
            filterOption={false}
            onSearch={keyword => handleCostCenterSearch(index, keyword)}
            onSelect={(_value, option) => handleCostCenterSelect(index, option)}
            onFocus={() => handleCostCenterSearch(index, String(value || ""))}
            notFoundContent={costCenterLoadingMap[index] ? <div style={{ textAlign: "center", padding: "8px 0" }}><Spin size="small" /></div> : "No Cost Center Master found"}
            style={{ width: "100%" }}
          >
            <Input placeholder="Search Cost Center or name" />
          </AutoComplete>
        ) : (
          value || "-"
        ),
    },
    {
      title: "Cost Center Name",
      dataIndex: "cost_center_name",
      key: "cost_center_name",
      width: 240,
      render: (value: ISapReservationDetail["cost_center_name"]): ReactNode => value || "-",
    },
  ]

  if (editable) {
    columns.push({
      title: "Action",
      key: "action",
      width: 100,
      align: "center",
      render: (_value: unknown, _record: ISapReservationDetail, index: number): ReactNode => (
        <Button danger type="link" icon={<DeleteOutlined />} onClick={() => removeRow(index)}>
          Remove
        </Button>
      ),
    })
  }

  return (
    <div>
      {editable ? (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
          <div style={{ color: "#8c8c8c", fontSize: 13 }}>Add all reservation line items before submitting. Every row requires Product ID, Qty, UOM, Long Description, and at least one of WBS or Cost Center. You can fill both when needed.</div>
          <Button icon={<PlusOutlined />} onClick={addRow}>
            Add Line
          </Button>
        </div>
      ) : null}

      {!items?.length ? <Empty description="No reservation items" /> : <Table columns={columns} dataSource={items} pagination={editable ? false : { pageSize: 5 }} rowKey={getRowKey} scroll={{ x: 1700 }} />}

      {editable && items?.length ? <Space style={{ color: "#8c8c8c", fontSize: 12 }}>* Mandatory line-item fields. WBS and Cost Center can both be filled, but at least one is required.</Space> : null}
    </div>
  )
}

export default ReservationItemsTable
